import React from 'react';
import { useQuery } from 'react-query';
import { Editor } from '@splashthat/splash-cms-engine';
import { SuiSpinner } from '@splashthat/sui';
import { getInstance as getBootstrap } from 'utils/bootstrap';
import { getLibrarianApiClient } from 'lib/SplashApiClient/subclients/LibrarianApiClient';

import { BLOCKS } from 'common/components/TouchpointEditor/constants/blocks';
import { WebRenderer } from 'common/components/TouchpointEditor/renderer/WebRenderer/WebRenderer';
import { TouchpointDataProvider } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import { LoadingSpinnerContainer } from 'common/components/TouchpointEditor/components/TouchpointContainer';
import { CONFIRMATIONS } from 'common/components/TouchpointEditor/constants/touchpoints';

import { confirmationsDefaultStructures } from 'common/compounds/ConfirmationsTouchpoint/constants/defaultStructures';
import { RSVP_YES_DOI_MESSAGE_DISPLAY_NAME } from 'common/compounds/ConfirmationsTouchpoint/constants/defaultStructures/rsvpYes';
import { addDoubleOptInMessage } from 'common/compounds/ConfirmationsTouchpoint/ConfirmationsTouchpoint';
import Portal from './Portal';

const bootstrap = getBootstrap();
const eventId = bootstrap.get('splashPage.eventId');
const storedConfirmationsTemplates = bootstrap.get('onPageConfirmations.states', []);
const isDoubleOptInEnabled = bootstrap.get('isDoubleOptInEnabled', false);
const isDoubleOptInFlagEnabled = bootstrap.get('flags.enabledDoubleOptIn', false);

// Function to handle the addition or visibility update of the Double Opt-In message
// in the confirmation structure
const handleDoubleOptInMessage = (components, isEnabled) => {
    const doiKey = Object.keys(components).find(
        (key) => components[key].props.meta.displayName === RSVP_YES_DOI_MESSAGE_DISPLAY_NAME,
    );

    // If DOI message exists and visibility needs to be updated, return a new object
    if (doiKey && components[doiKey].props.meta.visible !== isEnabled) {
        return {
            ...components,
            [doiKey]: {
                ...components[doiKey],
                props: {
                    ...components[doiKey].props,
                    meta: {
                        ...components[doiKey].props.meta,
                        visible: isEnabled,
                    },
                },
            },
        };
    }

    // If DOI message doesn't exist and should be enabled, create and insert it
    if (!doiKey && isEnabled) {
        return addDoubleOptInMessage(components);
    }

    // If no changes are needed, return the original components object
    return components;
};

export default function App({ serverResponse, triggerTypes } = {}) {
    const portal = React.useRef(null);
    const confirmationsApiClient = React.useRef(getLibrarianApiClient(eventId)).current;

    const { confirmation, tickets, guestAccessHash, sessionRegistrationConfirmation } = serverResponse;
    const confirmationState = triggerTypes.find((trigger) => storedConfirmationsTemplates.includes(trigger));
    const [defaultConfirmationState] = triggerTypes.slice(-1);

    const request = useQuery(
        ['confirmationStructure', confirmationState],
        () => confirmationsApiClient.getPublicEventPageConfirmationByState(confirmationState),
        {
            staleTime: Infinity,
            cacheTime: 0,
        },
    );

    const event = {
        confirmationsConfig: {
            ...(confirmation?.config || {}),
            tickets,
        },
        sessionRegistrationConfirmation,
        touchpoint: CONFIRMATIONS,
    };
    event.id = eventId;

    const guest = {
        guestAccessHash,
    };

    const baseTree = request?.data || confirmationsDefaultStructures[defaultConfirmationState];
    const effectiveConfirmationState = request?.data ? confirmationState : defaultConfirmationState;

    const initialTree =
        isDoubleOptInFlagEnabled && effectiveConfirmationState === 'rsvp-yes'
            ? handleDoubleOptInMessage(baseTree, isDoubleOptInEnabled)
            : baseTree;

    if (request.isLoading) {
        return (
            <LoadingSpinnerContainer>
                <SuiSpinner loading size="large" aria-label="loading" />
            </LoadingSpinnerContainer>
        );
    }

    return (
        <Editor portal={portal} initialTree={initialTree} blocks={BLOCKS} Renderer={WebRenderer}>
            <TouchpointDataProvider event={event} guest={guest}>
                <React.Suspense fallback={null}>
                    <Portal portal={portal} />
                </React.Suspense>
            </TouchpointDataProvider>
        </Editor>
    );
}
